import React, { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * ReferralBlock — small inline chip shown at the bottom of the user's OWN
 * profile. Fetches the user's referral code once and gives them a copy link.
 * Hovering the row reveals a styled tooltip explaining the reward rules.
 */
export default function ReferralBlock() {
  const [state, setState] = useState(null); // { code, reward_threshold, reward_handshakes, stats }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/me/referral").then((r) => {
      if (alive) setState(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!state?.code) return null;

  const link = `${window.location.origin}/?ref=${state.code}`;
  const threshold = state.reward_threshold || 500;
  const reward = state.reward_handshakes || 25;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Fallback — hidden input
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="mt-6 flex justify-center" data-testid="referral-block">
      <div className="relative group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFD70055] bg-[#0A051ACC] text-[11px] font-mono text-[#A89BCC]">
        <span>Your referral link:</span>
        <button
          data-testid="referral-copy-btn"
          onClick={copy}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#FFD70088] text-[#FFD700] hover:bg-[#FFD70011] transition-colors font-heading uppercase tracking-widest text-[10px]"
          aria-label="Copy referral link"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy Link"}
        </button>

        {/* Tooltip */}
        <div
          data-testid="referral-tooltip"
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-[260px] rounded-md border border-[#FFD70088] bg-[#0A051AF0] px-3 py-2 text-left opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 z-30"
          style={{ boxShadow: "0 0 14px #FFD70033" }}
        >
          <div className="font-heading uppercase tracking-widest text-[11px] text-[#FFD700] mb-1">
            Referral Reward
          </div>
          <div className="text-[10px] text-[#A89BCC] leading-snug">
            Invite a new player and earn{" "}
            <span className="text-[#FFD700] font-heading">{reward} Handshakes</span>{" "}
            when they reach{" "}
            <span className="text-[#9D00FF] font-heading">{threshold} HOF points</span>.
          </div>
          {state.stats?.rewards_earned > 0 && (
            <div className="mt-1 text-[9px] font-mono text-[#39FF14]">
              Earned: {state.stats.rewards_earned} · +{state.stats.handshakes_from_referrals} 🤝
            </div>
          )}
          {/* arrow */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 rotate-45 border-r border-b border-[#FFD70088]"
            style={{ background: "#0A051AF0", marginTop: -4 }}
          />
        </div>
      </div>
    </div>
  );
}
